import CustomTable from "@/components/Table/CustomTable";
import useDebounce from "@/hooks/useDebounce";
import usePagination from "@/hooks/usePagination";
import { useCTVChannels } from "@/modules/Presets/TargetingExpression/targetingExpression.hook";
import SearchIcon from "@mui/icons-material/Search";
import { Box, FormControlLabel, Switch, TextField } from "@mui/material";
import React, { useState } from "react";
import { FieldValues, Path, PathValue, useWatch } from "react-hook-form";
import {
  SubModule,
  SubModuleCommonProps,
} from "../../../targetingExpression.interface";
import ChannelsTableRow from "./ChannelsTableRow";
import { CtvChannel } from "./ctvChannels.interface";

const headers = [
  { id: "logo", label: "", noSort: true },
  { id: "channelName", label: "Channel" },
  { id: "categoryName", label: "Category" },
  { id: "subCategoryName", label: "Sub-Category" },
  { id: "description", label: "Description" },
  { id: "floorCPM", label: "Floor CPM" },
];

export default function CtvChannels<
  T extends FieldValues,
  U extends SubModule = "CTV_CHANNEL"
>({
  control,
  getValues,
  setValue,
  parentName,
}: {
  control: any; // fix type
  getValues: any; // fix type
  setValue: any; // fix type
  parentName: any; // fix type
}): JSX.Element {
  const { page, handleChangePage, handleChangeRowsPerPage, rowsPerPage } =
    usePagination();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500); // 500ms debounce delay

  const [onlySaved, setOnlySaved] = useState(false);

  const lineItemId = useWatch({
    control,
    name: "id",
  });
  const savedChannels = useWatch({
    control,
    name: "ctvChannels",
  });
  const { data, isLoading } = useCTVChannels({
    search: debouncedSearch,
    sortBy: debouncedSearch ? "SAVED_CHANNELS" : undefined,
    savedOnly: onlySaved,
    page,
    lineItemId,
    size: rowsPerPage,
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleOnlySavedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOnlySaved(e.target.checked);
  };
  const handleToggleSave = (channel: CtvChannel) => {
    const isCurrentlySaved = Boolean(savedChannels[channel.id]);
    const newSavedChannels = { ...savedChannels };
    const currentValues = getValues();
    const dealIdList =
      currentValues?.[parentName]?.app_site?.deal_id?.value?.included || [];
    const appNameList =
      currentValues?.[parentName]?.app_site?.app_name?.value?.included || [];
    const dealIds = channel?.Deals?.map((item) => item.dealId) ?? [];
    const appNames = channel?.Apps?.map((item) => item.name) ?? [];
    if (isCurrentlySaved) {
      delete newSavedChannels[channel.id];

      const updatedDealIds = dealIdList.filter(
        (id: string) => !dealIds.includes(id)
      );
      const updatedAppNames = appNameList.filter(
        (name: string) => !appNames.includes(name)
      );

      setValue(
        `${parentName}.app_site.deal_id.value.included` as Path<T>,
        updatedDealIds as unknown as PathValue<T, Path<T>>,
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );

      setValue(
        `${parentName}.app_site.app_name.value.included` as Path<T>,
        updatedAppNames as unknown as PathValue<T, Path<T>>,
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    } else {
      newSavedChannels[channel.id] = channel;

      const updatedDealIds = [...new Set([...dealIdList, ...dealIds])];
      const updatedAppNames = [...new Set([...appNameList, ...appNames])];

      setValue(
        `${parentName}.app_site.deal_id.value.included` as Path<T>,
        updatedDealIds as unknown as PathValue<T, Path<T>>,
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );

      setValue(
        `${parentName}.app_site.app_name.value.included` as Path<T>,
        updatedAppNames as unknown as PathValue<T, Path<T>>,
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    }

    setValue(
      `ctvChannels` as Path<T>,
      newSavedChannels as unknown as PathValue<T, Path<T>>,
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        padding: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        <TextField
          placeholder="Search by channel name, category, or subcategory"
          size="small"
          value={search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: "action.active" }} />
            ),
          }}
          sx={{
            flex: "1 1 300px",
            maxWidth: 500,
            minWidth: 250,
          }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={onlySaved}
              onChange={handleOnlySavedChange}
              color="primary"
              disabled={!lineItemId}
            />
          }
          label="Only Saved Channels"
          sx={{ flexShrink: 0 }}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.paper",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <CustomTable
          headers={headers}
          rowData={data?.rows || []}
          tableRowComponent={ChannelsTableRow}
          tableRowComponentProps={{
            onToggleSave: handleToggleSave,
            savedChannels,
            control,
          }}
          page={page}
          handleChangePage={handleChangePage}
          rowsPerPage={rowsPerPage}
          totalCount={data?.count || 0}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          isLoading={isLoading}
        />
      </Box>
    </Box>
  );
}

export type CtvChannelProps<T extends FieldValues, U> = U extends "CTV_CHANNEL"
  ? CtvChannelsProps<T>
  : never;
export interface CtvChannelsProps<T extends FieldValues>
  extends SubModuleCommonProps<T> {
  lineItemId?: string;
}
